/**
 * Equipment Types
 * Flat list is the source of truth for the Equipment Type selector.
 * DB hook kept for other consumers.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface EquipmentType {
  id: string;
  code: string;
  name: string;
  category: string;
  display_order: number;
  search_keywords: string[] | null;
  is_active: boolean;
}

export interface EquipmentFlatItem {
  name: string;
  code: string;
  enumValue: string;
}

/**
 * Canonical flat equipment list — source of truth for the Post Load selector.
 * 80 unique entries matching the user-provided freight equipment matrix.
 */
export const EQUIPMENT_FLAT_LIST: EquipmentFlatItem[] = [
  { name: 'Van', code: 'V', enumValue: 'van' },
  { name: 'Reefer', code: 'R', enumValue: 'reefer' },
  { name: 'Flatbed', code: 'F', enumValue: 'flatbed' },
  { name: 'Auto Carrier', code: 'AC', enumValue: 'auto_carrier' },
  { name: 'B-Train', code: 'BT', enumValue: 'b_train' },
  { name: 'Conestoga', code: 'CN', enumValue: 'conestoga' },
  { name: 'Container', code: 'C', enumValue: 'container' },
  { name: 'Container Insulated', code: 'CI', enumValue: 'container_insulated' },
  { name: 'Container Refrigerated', code: 'CR', enumValue: 'container_refrigerated' },
  { name: 'Conveyor', code: 'CV', enumValue: 'conveyor' },
  { name: 'Double Drop', code: 'DD', enumValue: 'double_drop' },
  { name: 'Drop Deck Landoll', code: 'LA', enumValue: 'drop_deck_landoll' },
  { name: 'Dump Trailer', code: 'DT', enumValue: 'dump_trailer' },
  { name: 'Flatbed Air-Ride', code: 'FA', enumValue: 'flatbed_air_ride' },
  { name: 'Flatbed Conestoga', code: 'FN', enumValue: 'flatbed_conestoga' },
  { name: 'Flatbed Double', code: 'F2', enumValue: 'flatbed_double' },
  { name: 'Flatbed HazMat', code: 'FZ', enumValue: 'flatbed_hazmat' },
  { name: 'Flatbed Hotshot', code: 'FH', enumValue: 'flatbed_hotshot' },
  { name: 'Flatbed Maxi', code: 'MX', enumValue: 'flatbed_maxi' },
  { name: 'Flatbed or Step Deck', code: 'FD', enumValue: 'flatbed_or_step_deck' },
  { name: 'Flatbed w/Chains', code: 'FC', enumValue: 'flatbed_with_chains' },
  { name: 'Flatbed w/Sides', code: 'FS', enumValue: 'flatbed_with_sides' },
  { name: 'Flatbed w/Tarps', code: 'FT', enumValue: 'flatbed_with_tarps' },
  { name: 'Flatbed w/Team', code: 'FM', enumValue: 'flatbed_with_team' },
  { name: 'Flatbed w/Pallet Exchange', code: 'FP', enumValue: 'flatbed_pallet_exchange' },
  { name: 'Flatbed (Over Dimension)', code: 'FO', enumValue: 'flatbed_over_dimension' },
  { name: 'Flatbed/Van/Reefer', code: 'FR', enumValue: 'flatbed_van_reefer' },
  { name: 'Hopper Bottom', code: 'HB', enumValue: 'hopper_bottom' },
  { name: 'Insulated Van or Reefer', code: 'IR', enumValue: 'insulated_van_or_reefer' },
  { name: 'Lowboy', code: 'LB', enumValue: 'lowboy' },
  { name: 'Lowboy or RGN', code: 'LR', enumValue: 'lowboy_or_rgn' },
  { name: 'Lowboy (Over Dimension)', code: 'LO', enumValue: 'lowboy_over_dimension' },
  { name: 'Moving Van', code: 'MV', enumValue: 'moving_van' },
  { name: 'Pneumatic', code: 'NU', enumValue: 'pneumatic' },
  { name: 'Power Only', code: 'PO', enumValue: 'power_only' },
  { name: 'Reefer Air-Ride', code: 'RA', enumValue: 'reefer_air_ride' },
  { name: 'Reefer Double', code: 'R2', enumValue: 'reefer_double' },
  { name: 'Reefer HazMat', code: 'RZ', enumValue: 'reefer_hazmat' },
  { name: 'Reefer Intermodal', code: 'RN', enumValue: 'reefer_intermodal' },
  { name: 'Reefer Logistics', code: 'RL', enumValue: 'reefer_logistics' },
  { name: 'Reefer or Vented Van', code: 'RV', enumValue: 'reefer_or_vented_van' },
  { name: 'Reefer w/Team', code: 'RM', enumValue: 'reefer_with_team' },
  { name: 'Reefer w/Pallet Exchange', code: 'RP', enumValue: 'reefer_pallet_exchange' },
  { name: 'Removable Gooseneck', code: 'RG', enumValue: 'removable_gooseneck' },
  { name: 'Step Deck', code: 'SD', enumValue: 'step_deck' },
  { name: 'Step Deck or RGN', code: 'SR', enumValue: 'step_deck_or_rgn' },
  { name: 'Stepdeck Conestoga', code: 'SN', enumValue: 'stepdeck_conestoga' },
  { name: 'Straight Box Truck', code: 'SB', enumValue: 'straight_box_truck' },
  { name: 'Stretch Trailer', code: 'ST', enumValue: 'stretch_trailer' },
  { name: 'Tanker Aluminum', code: 'TA', enumValue: 'tanker_aluminum' },
  { name: 'Tanker Intermodal', code: 'TN', enumValue: 'tanker_intermodal' },
  { name: 'Tanker Steel', code: 'TS', enumValue: 'tanker_steel' },
  { name: 'Truck and Trailer', code: 'TT', enumValue: 'truck_and_trailer' },
  { name: 'Van Air-Ride', code: 'VA', enumValue: 'van_air_ride' },
  { name: 'Van Conestoga', code: 'VS', enumValue: 'van_conestoga' },
  { name: 'Van Double', code: 'V2', enumValue: 'van_double' },
  { name: 'Van HazMat', code: 'VZ', enumValue: 'van_hazmat' },
  { name: 'Van Hotshot', code: 'VH', enumValue: 'van_hotshot' },
  { name: 'Van Insulated', code: 'VI', enumValue: 'van_insulated' },
  { name: 'Van Intermodal', code: 'VN', enumValue: 'van_intermodal' },
  { name: 'Van Lift-Gate', code: 'VG', enumValue: 'van_lift_gate' },
  { name: 'Van Logistics', code: 'VL', enumValue: 'van_logistics' },
  { name: 'Van Open-Top', code: 'OT', enumValue: 'van_open_top' },
  { name: 'Van or Flatbed w/Tarps', code: 'VT', enumValue: 'van_or_flatbed_tarps' },
  { name: 'Van or Flatbed', code: 'VF', enumValue: 'van_or_flatbed' },
  { name: 'Van or Reefer', code: 'VR', enumValue: 'van_or_reefer' },
  { name: 'Van Roller Bed', code: 'VB', enumValue: 'van_roller_bed' },
  { name: 'Van Triple', code: 'V3', enumValue: 'van_triple' },
  { name: 'Van Vented', code: 'VV', enumValue: 'van_vented' },
  { name: 'Van w/Blanket Wrap', code: 'VW', enumValue: 'van_blanket_wrap' },
  { name: 'Van w/Curtains', code: 'VC', enumValue: 'van_with_curtains' },
  { name: 'Van w/Pallet Exchange', code: 'VP', enumValue: 'van_pallet_exchange' },
  { name: 'Van w/Team', code: 'VM', enumValue: 'van_with_team' },
  { name: 'Sprinter Van', code: 'SV', enumValue: 'sprinter_van' },
  { name: 'Sprinter Van Hazmat', code: 'SZ', enumValue: 'sprinter_van_hazmat' },
  { name: 'Sprinter Van Temp-Controlled', code: 'SC', enumValue: 'sprinter_van_temp_controlled' },
  { name: 'Sprinter Van Team', code: 'SM', enumValue: 'sprinter_van_team' },
  { name: 'Straight Box Truck - Reefer', code: 'BR', enumValue: 'straight_box_reefer' },
  { name: 'Straight Box Truck Hazmat', code: 'BZ', enumValue: 'straight_box_hazmat' },
  { name: 'Power Only Towaway', code: 'PT', enumValue: 'power_only_towaway' },
  { name: 'Power Only Load Out', code: 'PL', enumValue: 'power_only_load_out' },
];

/** Fetch all active equipment types from the database, grouped by category */
export const useEquipmentTypes = () => {
  return useQuery({
    queryKey: ['equipment-types'],
    queryFn: async (): Promise<EquipmentType[]> => {
      const { data, error } = await supabase
        .from('equipment_types')
        .select('*')
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;
      return (data || []) as unknown as EquipmentType[];
    },
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  });
};

/** Group equipment types by category */
export const groupByCategory = (types: EquipmentType[]): Record<string, EquipmentType[]> => {
  return types.reduce((acc, type) => {
    if (!acc[type.category]) acc[type.category] = [];
    acc[type.category].push(type);
    return acc;
  }, {} as Record<string, EquipmentType[]>);
};

/** Format an equipment type enum value using the flat list */
export const formatEquipmentType = (enumValue: string, types?: EquipmentType[]): string => {
  // Try flat list first
  const flat = EQUIPMENT_FLAT_LIST.find(e => e.enumValue === enumValue);
  if (flat) return flat.name;

  if (types && types.length > 0) {
    const found = types.find(t => {
      const enumFromCode = codeToEnum(t.code);
      return enumFromCode === enumValue || t.name.toLowerCase() === enumValue.toLowerCase();
    });
    if (found) return found.name;
  }
  return enumValue.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/** Map a code (e.g. 'V') to its enum value (e.g. 'van') */
export const codeToEnum = (code: string): string => {
  const map: Record<string, string> = {
    AC: 'auto_carrier', SV: 'sprinter_van', SVH: 'sprinter_van_hazmat',
    SVT: 'sprinter_van_temp', SVTM: 'sprinter_van_team',
    BT: 'b_train', CN: 'conestoga', C: 'container',
    CI: 'container_insulated', CR: 'container_refrigerated',
    CV: 'conveyor', DD: 'double_drop', LA: 'drop_deck_landoll',
    DT: 'dump_trailer',
    F: 'flatbed', FA: 'flatbed_air_ride', FN: 'flatbed_conestoga',
    F2: 'flatbed_double', FZ: 'flatbed_hazmat', FH: 'flatbed_hotshot',
    MX: 'flatbed_maxi', FD: 'flatbed_or_step_deck',
    FO: 'flatbed_over_dimension', FC: 'flatbed_with_chains',
    FS: 'flatbed_with_sides', FT: 'flatbed_with_tarps',
    FM: 'flatbed_with_team', FP: 'flatbed_pallet_exchange', FR: 'flatbed_van_reefer',
    SBR: 'straight_box_reefer', SBH: 'straight_box_hazmat',
    IR: 'insulated_van_or_reefer', MV: 'moving_van',
    V: 'van', VA: 'van_air_ride', VS: 'van_conestoga', V2: 'van_double',
    R: 'reefer', RA: 'reefer_air_ride', R2: 'reefer_double',
    RZ: 'reefer_hazmat', RN: 'reefer_intermodal', RL: 'reefer_logistics',
    RM: 'reefer_with_team', RP: 'reefer_pallet_exchange',
    RV: 'reefer_or_vented_van',
    TA: 'tanker_aluminum', TS: 'tanker_steel', TN: 'tanker_intermodal',
    LB: 'lowboy', LO: 'lowboy_over_dimension', RG: 'removable_gooseneck',
    LR: 'lowboy_or_rgn',
    HB: 'hopper_bottom', NU: 'pneumatic',
    PO: 'power_only', POL: 'power_only_load_out', POT: 'power_only_tow_away',
    PL: 'power_only_load_out', PT: 'power_only_towaway',
    TT: 'truck_and_trailer',
    SD: 'step_deck', SR: 'step_deck_or_rgn', SN: 'stepdeck_conestoga',
    SB: 'straight_box_truck', ST: 'stretch_trailer',
    BR: 'straight_box_reefer', BZ: 'straight_box_hazmat',
    SZ: 'sprinter_van_hazmat', SC: 'sprinter_van_temp_controlled',
    SM: 'sprinter_van_team',
    VZ: 'van_hazmat', VH: 'van_hotshot', VI: 'van_insulated',
    VN: 'van_intermodal', VG: 'van_lift_gate', VL: 'van_logistics',
    OT: 'van_open_top', VT: 'van_or_flatbed_tarps', VF: 'van_or_flatbed',
    VR: 'van_or_reefer', VB: 'van_roller_bed', V3: 'van_triple',
    VV: 'van_vented', VW: 'van_blanket_wrap', VC: 'van_with_curtains',
    VP: 'van_pallet_exchange', VM: 'van_with_team',
  };
  return map[code] || code.toLowerCase();
};

/** Map an enum value (e.g. 'van') to its code (e.g. 'V') */
export const enumToCode = (enumValue: string): string => {
  // Try flat list first for canonical mapping
  const flat = EQUIPMENT_FLAT_LIST.find(e => e.enumValue === enumValue);
  if (flat) return flat.code;

  const map: Record<string, string> = {
    auto_carrier: 'AC', sprinter_van: 'SV', sprinter_van_hazmat: 'SZ',
    sprinter_van_temp: 'SVT', sprinter_van_team: 'SM',
    sprinter_van_temp_controlled: 'SC',
    b_train: 'BT', conestoga: 'CN', container: 'C',
    container_insulated: 'CI', container_refrigerated: 'CR',
    conveyor: 'CV', double_drop: 'DD', drop_deck_landoll: 'LA',
    dump_trailer: 'DT',
    flatbed: 'F', flatbed_air_ride: 'FA', flatbed_conestoga: 'FN',
    flatbed_double: 'F2', flatbed_hazmat: 'FZ', flatbed_hotshot: 'FH',
    flatbed_maxi: 'MX', flatbed_or_step_deck: 'FD', step_deck: 'SD',
    flatbed_over_dimension: 'FO', flatbed_with_chains: 'FC',
    flatbed_with_sides: 'FS', flatbed_with_tarps: 'FT',
    flatbed_with_team: 'FM', flatbed_pallet_exchange: 'FP', flatbed_van_reefer: 'FR',
    straight_box_reefer: 'BR', straight_box_hazmat: 'BZ',
    straight_box_truck: 'SB', stretch_trailer: 'ST',
    insulated_van: 'IR', insulated_van_or_reefer: 'IR', moving_van: 'MV',
    van: 'V', van_air_ride: 'VA', van_conestoga: 'VS', van_double: 'V2',
    van_hazmat: 'VZ', van_hotshot: 'VH', van_insulated: 'VI',
    van_intermodal: 'VN', van_lift_gate: 'VG', van_logistics: 'VL',
    van_open_top: 'OT', van_or_flatbed_tarps: 'VT', van_or_flatbed: 'VF',
    van_or_reefer: 'VR', van_roller_bed: 'VB', van_triple: 'V3',
    van_vented: 'VV', van_blanket_wrap: 'VW', van_with_curtains: 'VC',
    van_pallet_exchange: 'VP', van_with_team: 'VM',
    reefer: 'R', reefer_air_ride: 'RA', reefer_double: 'R2',
    reefer_hazmat: 'RZ', reefer_intermodal: 'RN', reefer_logistics: 'RL',
    reefer_with_team: 'RM', reefer_pallet_exchange: 'RP',
    reefer_or_vented_van: 'RV',
    tanker_aluminum: 'TA', tanker_steel: 'TS', tanker: 'TS', tanker_intermodal: 'TN',
    lowboy: 'LB', lowboy_over_dimension: 'LO', removable_gooseneck: 'RG',
    lowboy_or_rgn: 'LR', step_deck_or_rgn: 'SR', stepdeck_conestoga: 'SN',
    hopper_bottom: 'HB', pneumatic: 'NU',
    power_only: 'PO', power_only_load_out: 'PL', power_only_tow_away: 'POT',
    power_only_towaway: 'PT',
    truck_and_trailer: 'TT',
  };
  return map[enumValue] || enumValue.toUpperCase();
};

// Legacy exports for backward compatibility during migration
export const MVP_EQUIPMENT_TYPES = ['Van', 'Reefer', 'Flatbed'] as const;
export const MVP_EQUIPMENT_OPTIONS = [
  { value: 'van', label: 'Van' },
  { value: 'reefer', label: 'Reefer' },
  { value: 'flatbed', label: 'Flatbed' },
] as const;
export const EQUIPMENT_DISPLAY_NAMES: Record<string, string> = {};
export const isMVPEquipmentType = (_type: string): boolean => true;
